import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { usePatientsBase } from "../../../utils/hubPaths";
import { downloadExcelCsv, formatCsvDate } from "../../../utils/excelCsv";
import { ageFromDob } from "../../../utils/clientAppUtils";
import { Chip, ChipGroup } from "../../ui";
import aa from "../AdminPortal/adminApp.module.css";
import {
  ageLabel,
  dateTone,
  formatShortDate,
  genderShort,
  skillLevelLabel,
  sportRankLabel,
} from "./athleteQualifications";

const ROSTER_FILTERS = [
  { id: "all", label: "Все" },
  { id: "medical", label: "Без допуска" },
  { id: "rank", label: "Разряд истёк" },
];

function cardOf(client) {
  return client?.patient_card || {};
}

function medicalState(card) {
  if (!card?.medical_clearance) return "missing";
  const tone = dateTone(card.medical_clearance_expires_at, 14);
  if (tone === "expired") return "expired";
  if (!card.medical_clearance_expires_at) return "open";
  return tone;
}

function medicalText(card) {
  const state = medicalState(card);
  if (state === "missing") return "Нет";
  if (state === "expired") return "Просрочен";
  if (state === "open") return "Есть, без срока";
  return "Есть";
}

function toneOf(state) {
  if (state === "expired" || state === "missing") return aa.rosterBad;
  if (state === "soon" || state === "open") return aa.rosterSoon;
  if (state === "ok") return aa.rosterOk;
  return "";
}

export default function AthleteRosterTable({ clients, total }) {
  const patientsBase = usePatientsBase();
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => {
    return (clients || []).filter((client) => {
      const card = cardOf(client);
      if (filter === "medical") {
        const state = medicalState(card);
        return state === "missing" || state === "expired";
      }
      if (filter === "rank") {
        return !!card.sport_rank && dateTone(card.sport_rank_expires_at) === "expired";
      }
      return true;
    });
  }, [clients, filter]);

  const exportCsv = () => {
    const headers = [
      "ФИО",
      "Дата рождения",
      "Возраст",
      "Пол",
      "Телефон",
      "Группы",
      "Уровень",
      "Разряд",
      "Дата приказа",
      "Срок разряда",
      "Мед.допуск",
      "Допуск до",
      "Родитель",
      "Телефон родителя",
    ];
    const data = rows.map((client) => {
      const card = cardOf(client);
      const guardian = client.sport?.primary_guardian;
      const age = ageFromDob(client.date_of_birth);
      return [
        client.full_name || "",
        formatCsvDate(client.date_of_birth),
        age == null ? "" : ageLabel(age),
        genderShort(card.gender) === "—" ? "" : genderShort(card.gender),
        client.phone || "",
        (client.sport?.groups || []).join(", "),
        skillLevelLabel(card.skill_level_kind, card.skill_level, card.skill_level_label),
        sportRankLabel(card.sport_rank),
        formatCsvDate(card.sport_rank_order_date),
        formatCsvDate(card.sport_rank_expires_at),
        medicalText(card),
        formatCsvDate(card.medical_clearance_expires_at),
        guardian?.full_name || "",
        guardian?.phone || "",
      ];
    });
    downloadExcelCsv("Сводка-спортсмены.csv", headers, data);
  };

  return (
    <div className={aa.rosterBlock}>
      <div className={aa.rosterToolbar}>
        <ChipGroup label="Сводка">
          {ROSTER_FILTERS.map((item) => (
            <Chip
              key={item.id}
              selected={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </Chip>
          ))}
        </ChipGroup>
        <button type="button" className={aa.rosterExport} onClick={exportCsv}>
          Скачать
        </button>
      </div>
      {total > clients.length ? (
        <p className={aa.rosterHint}>
          Показаны {clients.length} из {total}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className={aa.rosterHint}>Никого нет</p>
      ) : (
      <div className={aa.rosterWrap}>
        <table className={aa.rosterTable}>
          <thead>
            <tr>
              <th className={aa.rosterName}>ФИО</th>
              <th>Дата рождения</th>
              <th>Возраст</th>
              <th>Пол</th>
              <th>Телефон</th>
              <th>Группа</th>
              <th>Уровень</th>
              <th>Разряд</th>
              <th>Дата приказа</th>
              <th>Срок разряда</th>
              <th>Мед.допуск</th>
              <th>Допуск до</th>
              <th>Родитель</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((client) => {
              const card = cardOf(client);
              const age = ageFromDob(client.date_of_birth);
              const rankTone = card.sport_rank
                ? dateTone(card.sport_rank_expires_at)
                : "empty";
              const med = medicalState(card);
              const guardian = client.sport?.primary_guardian;
              return (
                <tr key={client.id}>
                  <td className={aa.rosterName}>
                    <Link
                      to={`${patientsBase}/${client.id}`}
                      className={aa.rosterLink}
                    >
                      {client.full_name || "Без имени"}
                    </Link>
                  </td>
                  <td className={client.date_of_birth ? "" : aa.rosterBad}>
                    {client.date_of_birth
                      ? formatShortDate(client.date_of_birth)
                      : "нет"}
                  </td>
                  <td>{age == null ? "—" : ageLabel(age)}</td>
                  <td>{genderShort(card.gender)}</td>
                  <td>{client.phone || "—"}</td>
                  <td>{(client.sport?.groups || []).join(", ") || "—"}</td>
                  <td>
                    {skillLevelLabel(
                      card.skill_level_kind,
                      card.skill_level,
                      card.skill_level_label,
                    ) || "—"}
                  </td>
                  <td>{sportRankLabel(card.sport_rank) || "—"}</td>
                  <td>
                    {card.sport_rank
                      ? formatShortDate(card.sport_rank_order_date) || "—"
                      : "—"}
                  </td>
                  <td className={card.sport_rank ? toneOf(rankTone) : ""}>
                    {card.sport_rank
                      ? formatShortDate(card.sport_rank_expires_at) || "—"
                      : "—"}
                  </td>
                  <td className={toneOf(med)}>{medicalText(card)}</td>
                  <td className={toneOf(med === "ok" || med === "soon" || med === "expired" ? med : "")}>
                    {card.medical_clearance_expires_at
                      ? formatShortDate(card.medical_clearance_expires_at)
                      : "—"}
                  </td>
                  <td>
                    {guardian?.full_name
                      ? `${guardian.full_name}${guardian.phone ? ` · ${guardian.phone}` : ""}`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
